import { useCallback, useMemo } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { loginRequest, apiRequest } from '../config/auth.config';

export type UserRole = 'admin' | 'internal' | 'external';

export interface AuthUser {
  name: string;
  email: string;
  role: UserRole;
  oid?: string;
}

export interface UseAuthReturn {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: AuthUser | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
}

/**
 * Custom authentication hook wrapping MSAL functionality.
 * Provides user information, login/logout actions, and token acquisition.
 */
export function useAuth(): UseAuthReturn {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  const isLoading = inProgress !== InteractionStatus.None;

  const user = useMemo<AuthUser | null>(() => {
    if (!isAuthenticated || accounts.length === 0) return null;

    const account = accounts[0];
    const claims = account.idTokenClaims as Record<string, unknown> | undefined;

    // Extract role from token claims — common claim names for B2C
    let role: UserRole = 'external';
    if (claims) {
      const roleClaim =
        (claims['extension_Role'] as string) ||
        (claims['role'] as string) ||
        (claims['roles'] as string[] | undefined)?.[0] ||
        '';

      const normalizedRole = roleClaim.toLowerCase();
      if (normalizedRole === 'admin' || normalizedRole === 'administrator') {
        role = 'admin';
      } else if (normalizedRole === 'internal') {
        role = 'internal';
      } else {
        role = 'external';
      }
    }

    return {
      name: account.name || 'User',
      email: account.username || '',
      role,
      oid: claims?.oid as string | undefined,
    };
  }, [isAuthenticated, accounts]);

  const login = useCallback(async () => {
    try {
      await instance.loginRedirect(loginRequest);
    } catch (error) {
      console.error('[Auth] Login failed:', error);
    }
  }, [instance]);

  const logout = useCallback(async () => {
    try {
      await instance.logoutRedirect({
        postLogoutRedirectUri: window.location.origin,
      });
    } catch (error) {
      console.error('[Auth] Logout failed:', error);
    }
  }, [instance]);

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    const account = instance.getActiveAccount();
    if (!account) return null;

    try {
      const response = await instance.acquireTokenSilent({
        ...apiRequest,
        account,
      });
      return response.accessToken;
    } catch (error) {
      // If silent acquisition fails, try interactive
      try {
        await instance.acquireTokenRedirect(apiRequest);
        // After redirect, this won't return — page will reload
        return null;
      } catch (interactiveError) {
        console.error('[Auth] Token acquisition failed:', interactiveError);
        return null;
      }
    }
  }, [instance]);

  return {
    isAuthenticated,
    isLoading,
    user,
    login,
    logout,
    getAccessToken,
  };
}
